import { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
    Plus, Check, X, Trash2, Edit3, AlertTriangle, Clock,
    Circle, ClipboardList
} from 'lucide-react';
import { Button } from '@components/ui';
import DatePickerInput from '@components/ui/DatePickerInput';
import {
    gorevler as initialGorevler,
    oncelikler,
    kategoriler,
} from '@mocks/santiyeGunlugu';
import clsx from 'clsx';

const toISODate = (d) => d.toISOString().split('T')[0];
const today = toISODate(new Date());
const tomorrow = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return toISODate(d); })();

// Hızlı filtreler
const QUICK_FILTERS = [
    { key: 'bugun', label: 'Bugün' },
    { key: 'yarin', label: 'Yarın' },
    { key: 'bu_hafta', label: 'Bu Hafta' },
    { key: 'bu_ay', label: 'Bu Ay' },
    { key: 'aralık', label: 'Tarih Aralığı' },
];

function getHaftaAraligi() {
    const bugun = new Date();
    const bas = new Date(bugun);
    bas.setDate(bugun.getDate() - bugun.getDay() + 1);
    const son = new Date(bas);
    son.setDate(bas.getDate() + 6);
    return { bas: toISODate(bas), son: toISODate(son) };
}

function getAyAraligi() {
    const bugun = new Date();
    const bas = new Date(bugun.getFullYear(), bugun.getMonth(), 1);
    const son = new Date(bugun.getFullYear(), bugun.getMonth() + 1, 0);
    return { bas: toISODate(bas), son: toISODate(son) };
}

export default function Gorevlerim({ insaatId, gorevStats }) {
    const [gorevListesi, setGorevListesi] = useState(initialGorevler);
    const [activeFilter, setActiveFilter] = useState('bugun');
    const [baslangicDate, setBaslangicDate] = useState(null); // Date object (DatePickerInput)
    const [bitisDate, setBitisDate] = useState(null);
    const [yeniGorevMetin, setYeniGorevMetin] = useState('');
    const [yeniOncelik, setYeniOncelik] = useState('normal');
    const [yeniKategori, setYeniKategori] = useState('genel');
    const [editingId, setEditingId] = useState(null);
    const [editMetin, setEditMetin] = useState('');

    // Filtreleme
    const filteredGorevler = useMemo(() => {
        let data = insaatId ? gorevListesi.filter(g => g.insaatId === insaatId) : gorevListesi;

        if (activeFilter === 'bugun') {
            data = data.filter(g => g.tarih === today);
        } else if (activeFilter === 'yarin') {
            data = data.filter(g => g.tarih === tomorrow);
        } else if (activeFilter === 'bu_hafta') {
            const { bas, son } = getHaftaAraligi();
            data = data.filter(g => g.tarih >= bas && g.tarih <= son);
        } else if (activeFilter === 'bu_ay') {
            const { bas, son } = getAyAraligi();
            data = data.filter(g => g.tarih >= bas && g.tarih <= son);
        } else if (activeFilter === 'aralık') {
            const bas = baslangicDate ? toISODate(baslangicDate) : null;
            const son = bitisDate ? toISODate(bitisDate) : null;
            if (bas) data = data.filter(g => g.tarih >= bas);
            if (son) data = data.filter(g => g.tarih <= son);
        }

        const oncelikSira = { acil: 0, normal: 1, dusuk: 2 };
        return data.sort((a, b) => {
            if (a.tamamlandi !== b.tamamlandi) return a.tamamlandi ? 1 : -1;
            return (oncelikSira[a.oncelik] || 1) - (oncelikSira[b.oncelik] || 1);
        });
    }, [gorevListesi, activeFilter, baslangicDate, bitisDate, insaatId]);

    const bekleyen = filteredGorevler.filter(g => !g.tamamlandi).length;
    const tamamlanan = filteredGorevler.filter(g => g.tamamlandi).length;

    const addGorev = () => {
        const metin = yeniGorevMetin.trim();
        if (!metin) return;
        // Ekleme tarihi: aktif filtreye göre belirle
        let tarih = today;
        if (activeFilter === 'yarin') tarih = tomorrow;
        else if (activeFilter === 'aralık' && baslangicDate) tarih = toISODate(baslangicDate);

        setGorevListesi(prev => [{
            id: `g-${Date.now()}`,
            metin, tarih, oncelik: yeniOncelik, kategori: yeniKategori,
            tamamlandi: false, insaatId: insaatId || 'c-001', sefId: 'sef-1',
        }, ...prev]);
        setYeniGorevMetin('');
        toast.success('Görev eklendi');
    };

    const toggleTamamla = (id) =>
        setGorevListesi(prev => prev.map(g => g.id === id ? { ...g, tamamlandi: !g.tamamlandi } : g));

    const deleteGorev = (id) => setGorevListesi(prev => prev.filter(g => g.id !== id));

    const startEdit = (gorev) => { setEditingId(gorev.id); setEditMetin(gorev.metin); };
    const saveEdit = (id) => {
        if (!editMetin.trim()) return;
        setGorevListesi(prev => prev.map(g => g.id === id ? { ...g, metin: editMetin.trim() } : g));
        setEditingId(null);
    };
    const cancelEdit = () => setEditingId(null);

    const getOncelikConfig = (key) => ({
        acil: { bg: 'bg-error/10 text-error border-error/30', icon: AlertTriangle },
        normal: { bg: 'bg-warning/10 text-warning border-warning/30', icon: Clock },
        dusuk: { bg: 'bg-info/10 text-info border-info/30', icon: Circle },
    }[key] || { bg: 'bg-warning/10 text-warning border-warning/30', icon: Clock });

    const getKategoriConfig = (key) => kategoriler.find(k => k.key === key) || kategoriler[3];

    return (
        <div className="space-y-5">
            {/* Mini Stats */}
            <div className="grid grid-cols-3 gap-3">
                <div className="bg-bg-secondary rounded-xl p-3 text-center border border-border-light">
                    <p className="text-2xl font-bold text-warning">{gorevStats?.bugunBekleyen ?? bekleyen}</p>
                    <p className="text-xs text-text-muted mt-0.5">Bugün Bekleyen</p>
                </div>
                <div className="bg-bg-secondary rounded-xl p-3 text-center border border-border-light">
                    <p className="text-2xl font-bold text-success">{gorevStats?.bugunTamamlanan ?? tamamlanan}</p>
                    <p className="text-xs text-text-muted mt-0.5">Bugün Tamamlanan</p>
                </div>
                <div className="bg-bg-secondary rounded-xl p-3 text-center border border-border-light">
                    <p className="text-2xl font-bold text-error">{gorevStats?.acilBekleyen ?? 0}</p>
                    <p className="text-xs text-text-muted mt-0.5">Acil</p>
                </div>
            </div>

            {/* Hızlı Görev Ekleme */}
            <div className="bg-bg-secondary border border-border-light rounded-2xl p-4 space-y-3">
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={yeniGorevMetin}
                        onChange={(e) => setYeniGorevMetin(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGorev(); } }}
                        placeholder="Görev ekle... (Enter ile kaydet)"
                        className="flex-1 px-4 py-2.5 bg-bg-tertiary border border-border-light rounded-xl text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    />
                    <Button variant="primary" size="sm" onClick={addGorev} className="!rounded-xl shrink-0">
                        <Plus size={18} />
                    </Button>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs text-text-muted">Öncelik:</span>
                        <div className="flex gap-1">
                            {oncelikler.map(o => {
                                const cfg = getOncelikConfig(o.key);
                                return (
                                    <button key={o.key} onClick={() => setYeniOncelik(o.key)}
                                        className={clsx("px-2.5 py-1 text-xs rounded-lg border transition-all font-medium",
                                            yeniOncelik === o.key ? cfg.bg : "border-transparent text-text-muted hover:bg-bg-hover")}>
                                        {o.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs text-text-muted">Kategori:</span>
                        <div className="flex gap-1">
                            {kategoriler.map(k => (
                                <button key={k.key} onClick={() => setYeniKategori(k.key)}
                                    className={clsx("px-2.5 py-1 text-xs rounded-lg border transition-all font-medium",
                                        yeniKategori === k.key ? `${k.color} text-white border-transparent` : "border-transparent text-text-muted hover:bg-bg-hover")}>
                                    {k.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Filtre Satırı */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 flex-wrap">
                {/* Hızlı Filtreler */}
                <div className="flex items-center gap-1 bg-bg-tertiary rounded-xl p-1 flex-wrap">
                    {QUICK_FILTERS.map(f => (
                        <button key={f.key} onClick={() => setActiveFilter(f.key)}
                            className={clsx("px-3.5 py-1.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap",
                                activeFilter === f.key
                                    ? "bg-primary text-white shadow-sm"
                                    : "text-text-muted hover:text-text-primary")}>
                            {f.label}
                        </button>
                    ))}
                </div>
                {/* Sayaçlar */}
                <div className="flex items-center gap-3 text-sm text-text-muted">
                    <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-warning" />{bekleyen} bekleyen
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-success" />{tamamlanan} tamamlanan
                    </span>
                </div>
            </div>

            {/* Tarih Aralığı — sadece 'aralık' aktifken */}
            {activeFilter === 'aralık' && (
                <div className="flex items-center gap-3 p-3 bg-bg-secondary rounded-xl border border-border-light flex-wrap">
                    <span className="text-sm text-text-muted font-medium">Tarih aralığı:</span>
                    <div className="flex items-center gap-3 flex-1 flex-wrap">
                        <div className="flex-1 min-w-[160px]">
                            <DatePickerInput
                                value={baslangicDate}
                                onChange={setBaslangicDate}
                                placeholder="Başlangıç tarihi"
                                maxDate={bitisDate}
                                clearable
                            />
                        </div>
                        <span className="text-text-muted text-sm">—</span>
                        <div className="flex-1 min-w-[160px]">
                            <DatePickerInput
                                value={bitisDate}
                                onChange={setBitisDate}
                                placeholder="Bitiş tarihi"
                                minDate={baslangicDate}
                                clearable
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Görev Listesi */}
            {filteredGorevler.length > 0 ? (
                <div className="space-y-2">
                    {filteredGorevler.map((gorev) => {
                        const isEditing = editingId === gorev.id;
                        const oncelikCfg = getOncelikConfig(gorev.oncelik);
                        const kategoriCfg = getKategoriConfig(gorev.kategori);
                        const OncelikIcon = oncelikCfg.icon;

                        return (
                            <div key={gorev.id}
                                className={clsx(
                                    "group flex items-center gap-3 px-4 py-3 rounded-xl border transition-all",
                                    gorev.tamamlandi
                                        ? "bg-bg-tertiary/40 border-border-light opacity-55"
                                        : "bg-bg-secondary border-border-light hover:border-primary/20 hover:shadow-sm"
                                )}>
                                {/* Checkbox */}
                                <button onClick={() => toggleTamamla(gorev.id)}
                                    className={clsx("w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all",
                                        gorev.tamamlandi ? "bg-success border-success text-white" : "border-border-light hover:border-primary")}>
                                    {gorev.tamamlandi && <Check size={11} strokeWidth={3} />}
                                </button>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    {isEditing ? (
                                        <div className="flex gap-2">
                                            <input autoFocus type="text" value={editMetin}
                                                onChange={(e) => setEditMetin(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(gorev.id); if (e.key === 'Escape') cancelEdit(); }}
                                                className="flex-1 px-3 py-1 bg-bg-tertiary border border-primary rounded-lg text-text-primary text-sm focus:outline-none" />
                                            <button onClick={() => saveEdit(gorev.id)} className="p-1 text-success rounded hover:bg-success/10"><Check size={15} /></button>
                                            <button onClick={cancelEdit} className="p-1 text-text-muted rounded hover:bg-bg-hover"><X size={15} /></button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                            <span className={clsx("text-sm", gorev.tamamlandi ? "line-through text-text-muted" : "text-text-primary")}>
                                                {gorev.metin}
                                            </span>
                                            <div className="flex items-center gap-1.5 shrink-0">
                                                <span className={clsx("flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border font-medium", oncelikCfg.bg)}>
                                                    <OncelikIcon size={10} />
                                                    {oncelikler.find(o => o.key === gorev.oncelik)?.label}
                                                </span>
                                                <span className={clsx("px-2 py-0.5 rounded-md text-xs text-white font-medium", kategoriCfg.color)}>
                                                    {kategoriCfg.label}
                                                </span>
                                                {(activeFilter === 'bu_hafta' || activeFilter === 'bu_ay' || activeFilter === 'aralık') && (
                                                    <span className="text-xs text-text-muted">
                                                        {new Date(gorev.tarih).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', weekday: 'short' })}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                {!isEditing && (
                                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                        {!gorev.tamamlandi && (
                                            <button onClick={() => startEdit(gorev)}
                                                className="p-1.5 text-text-muted hover:text-primary hover:bg-primary/10 rounded-lg transition-colors">
                                                <Edit3 size={14} />
                                            </button>
                                        )}
                                        <button onClick={() => deleteGorev(gorev.id)}
                                            className="p-1.5 text-text-muted hover:text-error hover:bg-error/10 rounded-lg transition-colors">
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="text-center py-16 bg-bg-secondary rounded-2xl border border-border-light">
                    <ClipboardList size={48} className="mx-auto text-text-muted mb-4 opacity-40" />
                    <h3 className="text-lg font-medium text-text-primary mb-1">Bu dönem için görev yok</h3>
                    <p className="text-sm text-text-muted">Üstteki alandan hızlıca görev ekleyebilirsin.</p>
                </div>
            )}
        </div>
    );
}
